import { useLayoutEffect, useRef, useState } from "react";
import { Layout } from "./imports";

interface LayoutPlayerProps {
  layoutInfo?: Layout;
}

interface Font {
  family: string;
  url: string;
}

const LayoutPlayer = ({ layoutInfo }: LayoutPlayerProps) => {
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const html = layoutInfo?.html ?? ""
  const css = layoutInfo?.css ?? ""

  // Fetch fonts (runs only after html/css ready)
  useLayoutEffect(() => {
    if (!html || !css) return;

    const fontsApiUrl = (import.meta.env.VITE_SERVER_URL ?? "") + "/getFonts";

    let isCancelled = false;

    fetch(fontsApiUrl)
      .then((response) => response.json())
      .then((fonts: Font[]) => {
        if (isCancelled) return;

        const prevFragment = document.getElementById("style_div");
        if (prevFragment) {
          document.head.removeChild(prevFragment);
        }

        const fragment = document.createElement("div");
        fragment.id = "style_div";

        for (const font of fonts) {
          if (html.includes(font.family) || css.includes(font.family)) {
            const link = document.createElement("link");
            link.href = font.url;
            link.rel = "stylesheet";
            fragment.appendChild(link);
          }
        }

        document.head.appendChild(fragment);
      })
      .catch((err) => console.error("Failed to fetch fonts:", err));

    return () => {
      isCancelled = true;
      const cleanupFragment = document.getElementById("style_div");
      if (cleanupFragment) {
        document.head.removeChild(cleanupFragment);
      }
    };
  }, [html, css]);

  // Compute zoom and inject Tailwind + layout
  useLayoutEffect(() => {
    if (!layoutInfo || !html?.length || !css?.length) return;

    const availableWidth = Math.min(
      screen.availWidth,
      Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      )
    );
    const availableHeight = Math.min(
      screen.availHeight,
      Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      )
    );

    if (layoutInfo.height && layoutInfo.width) {
      const vertScale = availableHeight / layoutInfo.height;
      const horzScale = availableWidth / layoutInfo.width;
      setZoom(Math.min(vertScale, horzScale, 1)); // don't upscale
    }

    const _prevScript = document.getElementById("tailwind_script");
    if (!_prevScript) {
      const tailwind = document.createElement("script");
      tailwind.id = "tailwind_script";
      tailwind.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(tailwind);

      tailwind.onload = () => {
        onTailwindLoaded();
      };
    } else {
      onTailwindLoaded();
    }
  }, [layoutInfo, html, css]);

  function onTailwindLoaded() {
    if (!layoutInfo) return;
    const layoutDiv = layoutRef.current;
    if (!layoutDiv) return;

    // Apply styles
    const prevStyle = document.getElementById("layout_style");
    if (prevStyle) document.head.removeChild(prevStyle);

    const style = document.createElement("style");
    style.id = "layout_style";
    style.innerHTML = css ?? "";
    document.head.appendChild(style);

    // Render HTML
    let htmlToRender = html ?? "";
    let scripts: string[] = [];
    try {
      const pattern = /<script>([\s\S]*?)<\/script>/gi;
      scripts = htmlToRender.match(pattern) ?? [];
      htmlToRender = htmlToRender.replace(pattern, "");
      htmlToRender = htmlToRender.replace("<body", "<div");
      htmlToRender = htmlToRender.replace("</body>", "</div>");
    } catch (e) {
      console.error(e);
    }

    layoutDiv.innerHTML = htmlToRender;
    const child = layoutDiv.children[0];
    if (child) {
      child.setAttribute(
        "style",
        `height: ${layoutInfo.height}px;width: ${layoutInfo.width}px`
      );
    }

    // Inject scripts safely
    const prevFragment = document.getElementById("script_fragment");
    if (prevFragment) {
      document.head.removeChild(prevFragment);
    }

    const fragment = document.createElement("div");
    fragment.id = "script_fragment";

    for (let i = 0; i < scripts.length; i++) {
      const script = scripts[i];
      const scriptContent = script
        .replace(`<script>`, ``)
        .replace(`</script>`, ``);
      const scriptEl = document.createElement("script");
      scriptEl.textContent = `(function script${i}(){${scriptContent}}())`;
      fragment.appendChild(scriptEl);
    }

    document.head.appendChild(fragment);
  }

  return (
    <div
      ref={containerRef}
      id="backgroundElem"
      className="flex h-screen w-screen flex-col items-center justify-center bg-white"
    >
      <div
        className="bg-white"
        style={{
          transform: `scale(${zoom})`,
          transformOrigin: "center",
          width: layoutInfo?.width + "px",
          height: layoutInfo?.height + "px",
          position: "relative",
        }}
      >
        <div ref={layoutRef} className="text-3xl">
          Loading...
        </div>
      </div>
    </div>
  );
};

export default LayoutPlayer;
